import { useCallback, useEffect, useState } from 'react';
import { FieldErrors, UseFormTrigger } from 'react-hook-form';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import { MedicationFormStep, MedicationStepId } from './medicationFormSteps';

interface UseMedicationFormStepsArgs {
  steps: MedicationFormStep[];
  trigger: UseFormTrigger<MedicationsFormModalValuesProps>;
  isModalOpen: boolean;
  /** An existing medication is already valid, so every step is reachable. */
  isEditing: boolean;
}

export interface MedicationFormStepsState {
  activeStep: number;
  completedSteps: boolean[];
  isLastStep: boolean;
  goNext: () => Promise<void>;
  goBack: () => void;
  goToStep: (index: number) => void;
  /** Jump to the first step carrying an error, after a rejected submit. */
  goToFirstError: (errors: FieldErrors<MedicationsFormModalValuesProps>) => void;
}

export function useMedicationFormSteps({
  steps,
  trigger,
  isModalOpen,
  isEditing,
}: UseMedicationFormStepsArgs): MedicationFormStepsState {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<
    Partial<Record<MedicationStepId, boolean>>
  >({});
  // #692 rev B: "Magazyn" comes and goes with the drug picked in step 1 — it
  // must not send the vet back to the start or forget what they completed.
  // Only the rest of the layout (the form opening, an edit arriving) resets.
  const layoutKey = steps
    .filter((step) => step.id !== 'stock')
    .map((step) => step.id)
    .join('|');

  useEffect(() => {
    if (!isModalOpen) return;
    setActiveStep(0);
    // A check means "you have been through this", so it is never pre-filled —
    // otherwise the rail says nothing about where the vet actually is. Editing
    // instead unlocks jumping (see goToStep): the medication already holds a
    // valid answer for every step.
    setCompleted({});
  }, [isModalOpen, layoutKey]);

  // "Magazyn" can only go while the vet is in step 1, but never point past
  // the rail.
  const current = Math.min(activeStep, Math.max(steps.length - 1, 0));

  const goNext = useCallback(async () => {
    const step = steps[current];
    if (!step) return;
    const valid = await trigger(step.fields);
    if (!valid) return;
    setCompleted((done) => ({ ...done, [step.id]: true }));
    setActiveStep((index) => Math.min(index + 1, steps.length - 1));
  }, [current, steps, trigger]);

  const goBack = useCallback(
    () => setActiveStep((index) => Math.max(index - 1, 0)),
    [],
  );

  const goToStep = useCallback(
    (index: number) => {
      const target = steps[index];
      if (!target) return;
      if (isEditing || index <= current || completed[target.id]) {
        setActiveStep(index);
      }
    },
    [completed, current, isEditing, steps],
  );

  const goToFirstError = useCallback(
    (errors: FieldErrors<MedicationsFormModalValuesProps>) => {
      const failing = Object.keys(errors);
      const index = steps.findIndex((step) =>
        step.fields.some((field) => failing.includes(field as string)),
      );
      if (index >= 0) setActiveStep(index);
    },
    [steps],
  );

  /**
   * Editing an existing medication means every step already holds a valid
   * answer, so every step but the one being looked at reads as done. Deriving
   * it from where the vet has walked instead produced a rail that contradicted
   * itself — a jumped-over step grey between two checked ones — because the
   * rail is jumpable in the first place.
   *
   * Adding is the other way round: a step is done once it has been completed,
   * remembered by what the step is, so "Magazyn" arriving or leaving moves no
   * check onto its neighbour.
   */
  const resolvedCompletedSteps = steps.map((step, index) =>
    isEditing ? index !== current : Boolean(completed[step.id]),
  );

  return {
    activeStep: current,
    completedSteps: resolvedCompletedSteps,
    isLastStep: current === steps.length - 1,
    goNext,
    goBack,
    goToStep,
    goToFirstError,
  };
}
